// Las 5 pastillas de arriba de la tabla (`.stats-bar` del diseño): además de contar, filtran.
// Sin fuente muestran «—» y quedan deshabilitadas: filtrar una lista que no existe no significa nada.
// Lo mismo la pastilla suelta cuyo conteo no se pudo leer.
import { cn } from '@/lib/cn';
import { CREDITS_NOUN, loadedCountTitle, type LoadedScope } from '@/shared/patterns/LoadedScope';
import type { CreditTone } from '../../creditFile/logic';
import { PORTFOLIO_STAT_LABEL, PORTFOLIO_STAT_TONE, statCell } from '../format';
import { PORTFOLIO_STAT_KEYS, type PortfolioStatKey, type PortfolioStats } from '../types';

/** El número de cada pastilla lleva el color de su tono, como el mock. */
const VALUE_TONE: Record<CreditTone, string> = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    info: 'text-info',
    neutral: 'text-foreground',
};

/**
 * Las 5 se reparten el renglón en partes iguales (`flex: 1 1 0` del mock), no cada una su ancho:
 * cinco anchos distintos es lo que hacía ver la barra desordenada.
 */
const PILL =
    'inline-flex min-w-0 flex-1 basis-0 items-baseline justify-center gap-[5px] whitespace-nowrap rounded-[5px] border px-[9px] py-1 transition-colors';

/** Por qué la pastilla no filtra, o sobre qué cuenta cuando el número no es de toda la cartera. */
function pillNote(
    count: number | null,
    disabled: boolean,
    scope: LoadedScope | null,
): string | undefined {
    if (disabled) return 'Todavía no hay cartera que contar';
    if (count === null) return 'Este conteo no se pudo leer, así que la pastilla no filtra';
    if (scope === null) return undefined;
    return loadedCountTitle(undefined, scope, CREDITS_NOUN);
}

export interface PortfolioStatsBarProps {
    stats: PortfolioStats;
    active: PortfolioStatKey;
    onChange: (stat: PortfolioStatKey) => void;
    /** Sin fuente las pastillas informan pero no filtran. */
    disabled?: boolean;
    /** El universo contado. Con `hasMore` los conteos son de lo cargado, no de la cartera. */
    scope?: LoadedScope | null;
    className?: string;
}

export function PortfolioStatsBar({
    stats,
    active,
    onChange,
    disabled = false,
    scope = null,
    className,
}: PortfolioStatsBarProps) {
    return (
        <div
            className={cn(
                'flex flex-none items-center gap-1 border-b border-border bg-muted/15 px-3 py-1.5',
                className,
            )}
        >
            {PORTFOLIO_STAT_KEYS.map((key) => {
                // Un conteo «no se sabe» tampoco filtra: daría una lista vacía que no significa nada.
                const locked = disabled || stats[key] === null;
                const isActive = key === active && !locked;
                return (
                    <button
                        key={key}
                        type="button"
                        // `aria-disabled` y no `disabled`: la pastilla sigue enfocable, así que el
                        // lector de pantalla llega a su `title` y sabe por qué no filtra.
                        aria-disabled={locked}
                        title={pillNote(stats[key], disabled, scope)}
                        aria-pressed={isActive}
                        onClick={() => {
                            if (locked) return;
                            onChange(key);
                        }}
                        className={cn(
                            PILL,
                            'aria-disabled:cursor-default aria-disabled:opacity-70',
                            isActive
                                ? 'border-primary bg-accent'
                                : 'border-border bg-card hover:bg-accent/50',
                        )}
                    >
                        <span
                            className={cn(
                                'truncate text-[9.5px] font-bold uppercase tracking-[0.05em]',
                                isActive ? 'text-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {PORTFOLIO_STAT_LABEL[key]}
                        </span>
                        <span
                            className={cn(
                                'text-[12px] font-bold tabular-nums',
                                VALUE_TONE[PORTFOLIO_STAT_TONE[key]],
                            )}
                        >
                            {statCell(stats[key])}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
